import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
  getAgencies as fetchAgenciesApi,
  getIsBillToAgency as fetchIsBillToAgencyApi,
  getAgencyData,
  getAgencyDetailsById,
  getAgencyUsers,
} from '@/api/agency';
import {
  getListingUnreadMessageCounts,
  getTotalUnreadMessageCounts,
  type ListingUnreadMessageCount,
  type RoleInternalUnreadMessageCount,
} from '@/api/flatfox';
import { getLatestImport } from '@/api/importReport';
import { fetchToplistingCreditUsage, type ToplistingCreditUsage } from '@/api/toplistingService';
import auth0 from '@/auth/auth0Plugin';
import type { UserData } from '@/components/Agency/UserFormData/types';
import { agencyHasSomeProductCodes } from '@/helpers/agencyProductCodes';
import { mapUtcToHgRetsDateFormat } from '@/helpers/dateUtils';
import {
  BCS_OWNER_FALLBACK,
  getInitialAgencyState,
  type AgencyState,
  type AgencyUserMfaOptions,
} from '@/stores/types/agency';
import { useUserStore } from '@/stores/user/useUserStore';
import type { AccountOwner, AgencyDetails, AgencyProduct } from '@/types-agency';
import { AgencyProductCodes } from '@/types-agency';
import { captureError } from '@smg-real-estate/browser-rum';
import { OfferType, RankType } from '@smg-real-estate/hg-rets-schema';
import clone from 'lodash.clone';

const LOCALSTORAGE_CUSTOMER_ID_KEY = 'customer_id';

const hasImmoCredits = (payload: ToplistingCreditUsage | undefined): boolean =>
  !!payload &&
  [
    payload.initialCreditInCHFCents,
    payload.availableCreditInCHFCents,
    payload.usedCreditInCHFCents,
    payload.nextCreditInCHFCents,
  ].some((creditAmount) => creditAmount > 0);

export const useAgencyStore = defineStore(
  'agency',
  // eslint-disable-next-line max-statements -- setup store returns state, computed, and side-effect functions together
  () => {
    const initial = getInitialAgencyState();
    const activeAgencyId = ref(initial.activeAgencyId);
    const agencies = ref(initial.agencies);
    const activeProducts = ref(initial.activeProducts);
    const usersForActiveAgency = ref(initial.usersForActiveAgency);
    const isBillToAgency = ref(initial.isBillToAgency);
    const isImmoCreditsCustomer = ref(initial.isImmoCreditsCustomer);
    const bcsOwner = ref(initial.bcsOwner);
    const flatfoxListingUnreadMessageCounts = ref(initial.flatfoxListingUnreadMessageCounts);
    const flatfoxTotalUnreadMessageCount = ref(initial.flatfoxTotalUnreadMessageCount);
    const importSummary = ref(initial.importSummary);

    const products = (): Pick<AgencyState, 'activeProducts'> => ({
      activeProducts: activeProducts.value,
    });

    const activeAgency = computed<AgencyDetails | undefined>(() =>
      agencies.value.find((agency) => agency.customerId === activeAgencyId.value),
    );
    const isFlatfoxActive = computed<boolean>(() =>
      agencyHasSomeProductCodes([AgencyProductCodes.FLATFOX_INTEGRATION], products()),
    );
    const isFlatfoxApplicationFormActive = computed<boolean>(
      () =>
        agencyHasSomeProductCodes([AgencyProductCodes.FLATFOX_INTEGRATION], products()) &&
        agencyHasSomeProductCodes(
          [
            AgencyProductCodes.WELCOME,
            AgencyProductCodes.EXPERIENCED,
            AgencyProductCodes.PROFESSIONAL,
            AgencyProductCodes.EXPERT,
            AgencyProductCodes.SME,
          ],
          products(),
        ),
    );
    const isCasaleadActive = computed<boolean>(() =>
      agencyHasSomeProductCodes(
        [
          AgencyProductCodes.CASALEAD_DE,
          AgencyProductCodes.CASALEAD_FR,
          AgencyProductCodes.CASALEAD_IT,
        ],
        products(),
      ),
    );
    const isCasaleadPlusActive = computed<boolean>(() =>
      agencyHasSomeProductCodes(
        [AgencyProductCodes.EXPERT_DE, AgencyProductCodes.EXPERT_FR, AgencyProductCodes.EXPERT_IT],
        products(),
      ),
    );
    /**
     * AgencyProductCodes.EXPERT = GTM26 Gold
     * AgencyProductCodes.PROFESSIONAL = GTM26 Silver
     * AgencyProductCodes.EXPERIENCED = GTM26 Bronze
     * AgencyProductCodes.SME = GTM26 Flex
     */
    const isGoldPackage = computed<boolean>(() =>
      agencyHasSomeProductCodes([AgencyProductCodes.EXPERT], products()),
    );
    const isWelcomePackage = computed<boolean>(() =>
      agencyHasSomeProductCodes([AgencyProductCodes.WELCOME], products()),
    );
    const isSilverPackage = computed<boolean>(() =>
      agencyHasSomeProductCodes([AgencyProductCodes.PROFESSIONAL], products()),
    );
    const isBronzePackage = computed<boolean>(() =>
      agencyHasSomeProductCodes([AgencyProductCodes.EXPERIENCED], products()),
    );
    const isSMEPackage = computed<boolean>(() =>
      agencyHasSomeProductCodes([AgencyProductCodes.SME], products()),
    );
    const isStarterPackage = computed<boolean>(() =>
      agencyHasSomeProductCodes(
        [AgencyProductCodes.STARTER, AgencyProductCodes.LEGACY_STARTER],
        products(),
      ),
    );
    const isVisibilityProPackage = computed<boolean>(() =>
      agencyHasSomeProductCodes([AgencyProductCodes.VISIBILITY_PRO], products()),
    );
    const isTuttiCrossListingPackage = computed<boolean>(() =>
      agencyHasSomeProductCodes([AgencyProductCodes.RE_CROSS_TUTTI], products()),
    );
    const isAnibisCrossListingPackage = computed<boolean>(() =>
      agencyHasSomeProductCodes([AgencyProductCodes.RE_CROSS_ANIBIS], products()),
    );
    const flatfoxAgencyId = computed<string>(() => {
      const [product] = activeProducts.value.filter(
        (ap: AgencyProduct) => ap.productCode === AgencyProductCodes.FLATFOX_INTEGRATION,
      );

      return product?.agencyId;
    });
    const activeAgencyPersonId = computed<string | undefined>(() => activeAgency.value?.personId);
    const agenciesWithoutSelectedAgency = computed<AgencyDetails[]>(() =>
      agencies.value.filter((agency) => agency.customerId !== activeAgencyId.value),
    );
    const accountOwner = computed<AccountOwner>(() => ({
      email: bcsOwner.value?.email || BCS_OWNER_FALLBACK.email,
      ownerName: bcsOwner.value?.ownerName || BCS_OWNER_FALLBACK.ownerName,
      phone: bcsOwner.value?.phone || BCS_OWNER_FALLBACK.phone,
    }));
    const hasImportData = computed<boolean>(() => importSummary.value !== null);

    function findAgencyById(customerId: string): AgencyDetails | undefined {
      return agencies.value.find((agency) => agency.customerId === customerId);
    }

    function flatfoxListingUnreadMessageCountForInsertion(insertionId: string): number {
      const unreadMessageCount = flatfoxListingUnreadMessageCounts.value?.find(
        (count) => count.smg_listing === insertionId,
      );
      return unreadMessageCount?.count ?? 0;
    }

    function setActiveAgencyIdToLocalStorage(requestedAgencyId?: string): void {
      if (!requestedAgencyId) {
        localStorage.removeItem(LOCALSTORAGE_CUSTOMER_ID_KEY);
        return;
      }
      localStorage.setItem(LOCALSTORAGE_CUSTOMER_ID_KEY, requestedAgencyId);
    }

    function getAgencyFromLocalStorage(): string | null {
      return localStorage.getItem(LOCALSTORAGE_CUSTOMER_ID_KEY);
    }

    function markAgencyLogoAsBroken(customerId: string): void {
      const brokenCustomer = agencies.value.find((agency) => agency.customerId === customerId);
      if (brokenCustomer) {
        brokenCustomer.logoUrl = undefined;
      }
    }

    function setUpdatedRequiredMfaForActiveAgencyUser(options: AgencyUserMfaOptions): void {
      const { userId, requiredMFA } = options;
      const clonedUsers = clone(usersForActiveAgency.value);
      clonedUsers.forEach((userData: UserData) => {
        if (userData.userId === userId) {
          userData.requiredMFA = requiredMFA;
        }
      });
      usersForActiveAgency.value = clonedUsers;
    }

    function mergeFlatfoxListingUnreadMessageCounts(
      incomingCounts: ListingUnreadMessageCount[],
    ): void {
      const existingMap = new Map(
        flatfoxListingUnreadMessageCounts.value.map((count) => [count.smg_listing, count]),
      );
      incomingCounts.forEach((count) => {
        existingMap.set(count.smg_listing, count);
      });
      flatfoxListingUnreadMessageCounts.value = Array.from(existingMap.values());
    }

    function setFlatfoxTotalUnreadMessageCount(
      unreadCounts: RoleInternalUnreadMessageCount[] | null,
    ): void {
      if (!unreadCounts) {
        flatfoxTotalUnreadMessageCount.value = null;
        return;
      }

      const nonInternalCount = unreadCounts.find((unreadCount) => !unreadCount.internal);
      flatfoxTotalUnreadMessageCount.value = nonInternalCount?.count ?? 0;
    }

    async function setActiveAgencyId(customerId: string): Promise<void> {
      activeAgencyId.value = customerId;
      setActiveAgencyIdToLocalStorage(customerId);
    }

    async function fetchAgencies(): Promise<void> {
      try {
        const userId = useUserStore().auth0Id;
        const payload = await fetchAgenciesApi(userId);

        agencies.value = payload;
      } catch (e) {
        captureError(e);
        agencies.value = [];
      }
    }

    async function fetchAgencyById(agencyId: string): Promise<AgencyDetails | null> {
      const newAgency = await getAgencyDetailsById(agencyId);
      if (newAgency) {
        agencies.value.push(newAgency);
      }
      return newAgency;
    }

    async function fetchAndSetUsersForAgency(agencyId?: string): Promise<void> {
      const users = await getAgencyUsers(agencyId as string);
      usersForActiveAgency.value = users;
    }

    async function getAgencyProductsAndOwner(customerId: string): Promise<void> {
      const agencyData = await getAgencyData(customerId);
      if (!agencyData) {
        console.warn('Cannot fetch agency data for', customerId);
      }
      activeProducts.value = agencyData?.activeProducts || [];
      bcsOwner.value = agencyData?.bcsOwner;
    }

    async function retrieveLastAgencyId(): Promise<string | null> {
      return activeAgencyId.value || getAgencyFromLocalStorage();
    }

    async function resolveAgency(requestedAgencyId?: string): Promise<AgencyDetails | null> {
      const agencyId = requestedAgencyId || (await retrieveLastAgencyId());
      if (useUserStore().hasCCCGroup) {
        return findAgencyById(agencyId as string) || (await fetchAgencyById(agencyId as string));
      }
      return findAgencyById(agencyId as string) || agencies.value[0];
    }

    async function fetchIsBillToAgency(): Promise<void> {
      const userIsNotAssignedToAnAgency = activeAgencyId.value === undefined;

      if (userIsNotAssignedToAnAgency) return;

      try {
        isBillToAgency.value = await fetchIsBillToAgencyApi();
      } catch (e) {
        captureError(e);
      }
    }

    async function fetchIsImmoCreditsCustomer(): Promise<void> {
      const userIsNotAssignedToAnAgency = activeAgencyId.value === undefined;

      if (userIsNotAssignedToAnAgency) {
        isImmoCreditsCustomer.value = false;
        return;
      }

      isImmoCreditsCustomer.value = false;

      try {
        const payload = await fetchToplistingCreditUsage(
          OfferType.Rent,
          RankType.Top,
          mapUtcToHgRetsDateFormat(new Date().toISOString()),
          null,
        );

        isImmoCreditsCustomer.value = hasImmoCredits(payload);
      } catch (e) {
        captureError(e);
      }
    }

    async function fetchImportSummary(): Promise<void> {
      try {
        importSummary.value = await getLatestImport(false);
      } catch {
        importSummary.value = null;
      }
    }

    async function switchToAgency(requestedAgencyId?: string): Promise<AgencyDetails | null> {
      const agencyDetails = await resolveAgency(requestedAgencyId);
      if (!agencyDetails) {
        return null;
      }

      const agencyId = agencyDetails.customerId as string;
      await getAgencyProductsAndOwner(agencyId);

      await auth0.getAccessTokenSilently({
        cacheMode: 'off',
        authorizationParams: {
          selectedCustomerId: agencyId,
          selectedPersonId: agencyDetails?.personId,
        },
      });

      await setActiveAgencyId(agencyId);

      fetchIsBillToAgency();
      fetchIsImmoCreditsCustomer();
      fetchImportSummary();

      return agencyDetails;
    }

    async function fetchFlatfoxUnreadMessageCounts(listingIds: string[]): Promise<void> {
      const customerId = activeAgencyId.value;
      if (!customerId) {
        flatfoxListingUnreadMessageCounts.value = [];
        flatfoxTotalUnreadMessageCount.value = null;
        return;
      }

      const [listingCountsResult, totalCountsResult] = await Promise.allSettled([
        listingIds.length ? getListingUnreadMessageCounts(listingIds, customerId) : [],
        getTotalUnreadMessageCounts(customerId),
      ]);

      if (listingCountsResult.status === 'fulfilled') {
        mergeFlatfoxListingUnreadMessageCounts(listingCountsResult.value);
      } else {
        flatfoxListingUnreadMessageCounts.value = [];
        captureError(listingCountsResult.reason);
      }

      if (totalCountsResult.status === 'fulfilled') {
        setFlatfoxTotalUnreadMessageCount(totalCountsResult.value);
      } else {
        setFlatfoxTotalUnreadMessageCount(null);
        captureError(totalCountsResult.reason);
      }
    }

    return {
      activeAgencyId,
      agencies,
      activeProducts,
      usersForActiveAgency,
      isBillToAgency,
      isImmoCreditsCustomer,
      bcsOwner,
      flatfoxListingUnreadMessageCounts,
      flatfoxTotalUnreadMessageCount,
      importSummary,
      activeAgency,
      isFlatfoxActive,
      isFlatfoxApplicationFormActive,
      isCasaleadActive,
      isCasaleadPlusActive,
      isGoldPackage,
      isVisibilityProPackage,
      isWelcomePackage,
      isSilverPackage,
      isBronzePackage,
      isSMEPackage,
      isStarterPackage,
      isTuttiCrossListingPackage,
      isAnibisCrossListingPackage,
      flatfoxAgencyId,
      activeAgencyPersonId,
      agenciesWithoutSelectedAgency,
      accountOwner,
      hasImportData,
      findAgencyById,
      flatfoxListingUnreadMessageCountForInsertion,
      setActiveAgencyIdToLocalStorage,
      getAgencyFromLocalStorage,
      markAgencyLogoAsBroken,
      setUpdatedRequiredMfaForActiveAgencyUser,
      mergeFlatfoxListingUnreadMessageCounts,
      setFlatfoxTotalUnreadMessageCount,
      setActiveAgencyId,
      fetchAgencies,
      fetchAgencyById,
      fetchAndSetUsersForAgency,
      getAgencyProductsAndOwner,
      retrieveLastAgencyId,
      resolveAgency,
      fetchIsBillToAgency,
      fetchIsImmoCreditsCustomer,
      fetchImportSummary,
      switchToAgency,
      fetchFlatfoxUnreadMessageCounts,
    };
  },
);
